import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupLabel,
} from "@babackapp/customer-ui/components/input-group";
import { Separator } from "@babackapp/customer-ui/components/separator";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircleIcon, ArrowLeft, LoaderIcon } from "lucide-react";
import { motion } from "motion/react";
import { Controller, useForm } from "react-hook-form";
import { useFocusFirstError } from "../hooks/use-focus-first-error";
import { useLocation, useNavigate } from "react-router-dom";
import { useCustomText } from "../hooks/use-custom-text";
import { usePublicCreateGiftReturn } from "@babackapp/private-api-client/client/use-public-create-gift-return";
import { CountryCombobox } from "../components/country-combobox";
import { deriveCountryName } from "../utils/countries";
import {
  giftAddressSchema,
  type GiftAddressFormValues,
} from "../forms/gift-address.form";

type LocationState = {
  token?: string;
  giftCode?: string;
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
};

const COUNTRY_LABEL_ID = "gift-address-country-label";

type GiftAddressField = keyof GiftAddressFormValues;

// Every schema field, so a rule added later still gets its error line: the
// Record type fails to compile on a field without a label.
const FIELD_LABELS: Record<GiftAddressField, string> = {
  first_name: "common.fields.firstName",
  last_name: "common.fields.lastName",
  address1: "common.fields.address1",
  address2: "common.fields.address2",
  city: "common.fields.city",
  zip: "common.fields.zip",
  country_code: "common.fields.country",
};
const FIELDS_IN_ORDER = [
  "first_name",
  "last_name",
  "address1",
  "address2",
  "city",
  "zip",
  "country_code",
] as const satisfies readonly GiftAddressField[];

const giftAddressErrorId = (name: GiftAddressField) =>
  `gift-address-${name}-error`;

export function GiftAddressPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: LocationState };
  const mutation = usePublicCreateGiftReturn();
  const t = useCustomText();

  const form = useForm<GiftAddressFormValues>({
    resolver: zodResolver(giftAddressSchema),
    defaultValues: {
      last_name: state?.last_name ?? "",
      first_name: state?.first_name ?? "",
      address2: "",
      country_code: "FR",
    },
    // useFocusFirstError focuses the field once its message is tied (BBS-438).
    shouldFocusError: false,
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;
  useFocusFirstError(form, FIELDS_IN_ORDER);

  const onSubmit = async (values: GiftAddressFormValues) => {
    await mutation.mutateAsync({
      token: state?.token,
      giftCode: state?.giftCode,
      email: state?.email,
      phone: state?.phone,
      address: {
        ...values,
        country: deriveCountryName(values.country_code),
      },
    });
    navigate("../complete");
  };

  const errorCode = (mutation.error as { code?: string } | null)?.code;

  // The schema only says "not empty": name the field. The summary is a live
  // region, announced on submit; each field also points at its line (BBS-438).
  const fieldErrors = FIELDS_IN_ORDER.filter((name) => errors[name]);
  const errorProps = (name: GiftAddressField) => {
    const invalid = !!errors[name];
    return {
      "aria-invalid": invalid || undefined,
      "aria-describedby": invalid ? giftAddressErrorId(name) : undefined,
    };
  };

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("gift.address.title")}</HeaderTitle>
        <HeaderDescription>{t("gift.address.description")}</HeaderDescription>
      </Header>

      {mutation.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertTitle>
            {errorCode === "GIFT_CODE_NOT_FOUND"
              ? t("gift.error.notFound")
              : errorCode === "GIFT_CODE_USED"
                ? t("gift.error.alreadyUsed")
                : t("common.error.retry")}
          </AlertTitle>
          <AlertDescription>{t("gift.error.checkAndRetry")}</AlertDescription>
        </Alert>
      )}

      {fieldErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {fieldErrors.map((name) => (
                <li key={name} id={giftAddressErrorId(name)}>
                  {t("common.error.fieldRequired", {
                    field: t(FIELD_LABELS[name]),
                  })}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-6 gap-2"
        >
          <Controller
            control={control}
            name="first_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.firstName")}
                  {...errorProps("first_name")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.firstName")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="last_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.lastNameShort")}
                  {...errorProps("last_name")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.lastName")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />

          <Separator className="col-span-6 my-4" />

          <Controller
            control={control}
            name="address1"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address1")}
                  {...errorProps("address1")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.address1")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="address2"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address2Placeholder")}
                  {...errorProps("address2")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>
                    {t("common.fields.address2")}
                  </InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="city"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.city")}
                  {...errorProps("city")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.city")}</InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="zip"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-3 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.zip")}
                  {...errorProps("zip")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.zip")}</InputGroupLabel>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="country_code"
            render={({ field }) => (
              <div className="col-start-5 col-span-2 flex flex-col gap-1">
                <span
                  id={COUNTRY_LABEL_ID}
                  className="text-xs text-muted-foreground px-0.5"
                >
                  {t("common.fields.country")}
                </span>
                <CountryCombobox
                  labelledBy={COUNTRY_LABEL_ID}
                  {...errorProps("country_code")}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("common.fields.country")}
                />
              </div>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderIcon className="animate-spin" />
              ) : (
                t("gift.address.cta")
              )}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
